'use client';

import { PauseWhite, PlayWhite, ProLiquidGlass, StarGlare } from 'assets/icons';
import {
  ComponentPropsWithoutRef,
  useEffect,
  useRef,
  useState,
} from 'react';
import { getCompressedAssetUrl } from 'lib/config/assetUrls';
import { Container } from '..';
import { PrimaryButton } from './styleConstants';
import styles from './HeroSectionV2.module.css';
import { HsaFsaHorizontal } from 'assets/icons/HsaFsaHorizontal';
import { useRouter, useSearchParams } from 'next/navigation';
import { useRegionLocale } from 'contexts/RegionLocaleContext';
import useIsCnOPlus from 'hooks/pricing/useIsCnOPlus';
import PlusLiquidGlass from 'assets/icons/PlusLiquidGlass';

const CTAButton = ({
  className,
  ...props
}: ComponentPropsWithoutRef<'button'>) => (
  <PrimaryButton
    className={`light ${styles.ctaButton}${className ? ` ${className}` : ''}`}
    {...props}
  />
);

export const HeroSectionV2 = ({
  className,
}: // onCtaClick,
{
  className?: string;
  // onCtaClick?: () => void;
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const sectionRef = useRef<HTMLElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [hasPlayedOnce, setHasPlayedOnce] = useState(false);
  const router = useRouter();
  const searchParams = useSearchParams();
  const { region: country } = useRegionLocale();
  const { regionSlug } = useRegionLocale();
  const { getValue } = useIsCnOPlus();

  const handlePlayPause = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
        setIsPlaying(false);
      } else {
        // If video has ended, reset to beginning for manual replay
        if (hasPlayedOnce) {
          videoRef.current.currentTime = 0;
        }
        videoRef.current.play();
        setIsPlaying(true);
      }
    }
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        const isIntersecting = entry.isIntersecting;

        if (videoRef.current) {
          if (isIntersecting) {
            // Only autoplay if the video hasn't played once yet
            if (!hasPlayedOnce) {
              videoRef.current.play().catch(() => {
                if (videoRef.current) videoRef.current.controls = true;
              });
              setIsPlaying(true);
            }
          } else {
            videoRef.current.pause();
            setIsPlaying(false);
          }
        }
      },
      {
        threshold: 0.5,
        rootMargin: '0px',
      },
    );

    // Handle video ending - pause at final frame
    const handleVideoEnded = () => {
      setIsPlaying(false);
      setHasPlayedOnce(true);
    };

    const currentSection = sectionRef.current;
    const currentVideo = videoRef.current;

    if (currentSection) {
      observer.observe(currentSection);
    }

    if (currentVideo) {
      currentVideo.addEventListener('ended', handleVideoEnded);
    }

    return () => {
      if (currentSection) {
        observer.unobserve(currentSection);
      }
      observer.disconnect();
      if (currentVideo) {
        currentVideo.removeEventListener('ended', handleVideoEnded);
      }
    };
  }, [hasPlayedOnce]);

  const videoSrc = getCompressedAssetUrl(
    getValue(
      '/web_v2/womens-health/videos/cno-pro-hero-v4.mp4',
      '/web_v2/womens-health/plus/hero/animation.mp4',
    ),
  );
  const videoResponsiveSrc = getCompressedAssetUrl(
    getValue(
      '/web_v2/womens-health/videos/cno-pro-hero-v4.mp4',
      '/web_v2/womens-health/plus/hero/animation.mp4',
    ),
  );

  const navigateToRing = () => {
    const qs = searchParams?.toString();
    router.push(`/${regionSlug}/ring/buy${qs ? `?${qs}` : ''}`);
  };

  const TagIcon = () =>
    getValue(
      <ProLiquidGlass
        style={{ height: '28px', width: 'auto' }}
        aria-hidden="true"
      />,
      <PlusLiquidGlass
        style={{ height: '28px', width: 'auto' }}
        aria-hidden="true"
      />,
    );

  const title = getValue(
    "The world's most accurate Cycle & Ovulation Tracker",
    'A science-backed approach to cycle and ovulation pattern tracking',
  );

  return (
    <section
      ref={sectionRef}
      className={
        className
          ? `${styles.sectionContainer} ${className}`
          : styles.sectionContainer
      }
    >
      <Container>
        <div className={styles.contentWrapper}>
          <div className={styles.topSection}>
            <p className={styles.caption}>
              Cycle and Ovulation <TagIcon />
              <StarGlare
                style={{
                  position: 'absolute',
                  top: '-100%',
                  right: '-36px',
                }}
                aria-hidden="true"
              />
            </p>
            <h1 className={styles.title}>{title}</h1>
            {country !== 'US' ? (
              <CTAButton
                onClick={navigateToRing}
                aria-label="Buy Ultrahuman Ring"
              >
                Buy Ring
              </CTAButton>
            ) : null}
          </div>

          <div className={styles.videoContainer}>
            <video
              ref={videoRef}
              muted
              // loop
              playsInline
              // poster="/web_v2/womens-health/video-poster-mobile.jpg"
              aria-label="Product demonstration video showing Ultrahuman Ring cycle tracking features"
              // preload="none" // WEB ACCESSIBILITY MAINTENANCE : Added preload='none' | Respects user's data preferences and reduces motion
            >
              {/* Desktop landscape video */}
              <source
                src={videoSrc}
                type="video/mp4"
                media="(min-width: 768px)"
              />
              {/* Mobile portrait video */}
              <source
                src={videoResponsiveSrc}
                type="video/mp4"
                media="(max-width: 767px)"
              />
              {/* Fallback video */}
              <source src={videoSrc} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </div>
          <button
            className={styles.playPauseButton}
            onClick={handlePlayPause}
            aria-label="Play or Pause Video"
            aria-pressed={isPlaying ? 'true' : 'false'}
            // onKeyDown={handleKeyDown} // WEB ACCESSIBILITY MAINTENANCE : Added keyboard handler | Need to implement Space/Enter key support for video control
          >
            <div className={styles.playPauseBg}>
              <div className={styles.playPauseIcon}>
                {isPlaying ? (
                  <PauseWhite aria-hidden="true" />
                ) : (
                  <PlayWhite aria-hidden="true" />
                )}
              </div>
            </div>
          </button>
        </div>
      </Container>

      {country === 'US' ? (
        <div
          className={styles.hsaBanner}
          role="banner"
          aria-label="HSA and FSA payment options available"
        >
          <HsaFsaHorizontal aria-hidden="true" />
          <p>Save more with your pre-tax dollars.</p>
        </div>
      ) : null}
    </section>
  );
};

/*
WEB ACCESSIBILITY MAINTENANCE : STRUCTURAL CHANGES NEEDED

1. HEADING HIERARCHY ✅
   - Current: Using h2 for main title
   - Required: Should be h1 if this is the main page heading
   - Action: Verify heading hierarchy context and consider changing Title to h1 if appropriate

2. SEMANTIC HTML STRUCTURE ✅
   - Current: Caption div could be more semantic
   - Required: Consider using proper semantic elements
   - Action: Consider using <p> or <span> with appropriate ARIA labeling for caption

3. KEYBOARD NAVIGATION
   - Current: PlayPauseButton missing keyboard event handlers
   - Required: Full keyboard support for video controls
   - Action: Implement handleKeyDown function to handle Space and Enter keys for play/pause functionality

4. FOCUS MANAGEMENT
   - Current: No visible focus indicators specified
   - Required: Ensure focus states are visible
   - Action: Add CSS focus styles for PlayPauseButton and CTAButton

5. VIDEO ACCESSIBILITY
   - Current: Video lacks comprehensive accessibility features
   - Required: Full video accessibility support
   - Action:
     * Add captions file and implement closed captioning
     * Consider adding video description track for visual content
     * Implement proper ARIA announcements for video state changes
     * Add option to disable autoplay for users with motion sensitivities

6. MOTION ACCESSIBILITY
   - Current: Video autoplays and has animations
   - Required: Respect user's motion preferences
   - Action: Check for prefers-reduced-motion media query and disable autoplay/animations accordingly

7. COLOR AND CONTRAST
   - Current: Text over dark background
   - Required: Ensure sufficient contrast ratios
   - Action: Verify all text meets WCAG 2.2 contrast requirements (4.5:1 for normal text, 3:1 for large text)

8. RESPONSIVE ACCESSIBILITY
   - Current: Component uses viewport units and complex responsive behavior
   - Required: Ensure functionality at 400% zoom
   - Action: Test component functionality at high zoom levels, ensure no content is cut off
*/

/*
WEB ACCESSIBILITY MAINTENANCE : TESTING CHECKLIST

MANUAL TESTING REQUIRED:
□ Keyboard navigation (Tab to CTAButton and PlayPauseButton, Enter/Space to activate)
□ Screen reader testing (VoiceOver, NVDA, or JAWS) for all content announcements
□ Video controls accessible via keyboard
□ 200-400% zoom testing for content reflow
□ High contrast mode testing
□ Motion sensitivity testing (prefers-reduced-motion)
□ Video captions display correctly when implemented

AUTOMATED TESTING:
□ Run eslint-plugin-jsx-a11y checks
□ Lighthouse accessibility audit
□ axe-core accessibility testing
□ Color contrast validation

FOCUS TESTING:
□ Focus visible on CTAButton
□ Focus visible on PlayPauseButton
□ Focus moves logically through interactive elements
□ No focus traps or lost focus issues

VIDEO TESTING:
□ Video controls announce state changes
□ Video can be paused via keyboard
□ Video respects user's autoplay preferences
□ Captions are available and functional (when implemented)
*/
